import { useEffect, useState, type FormEvent } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  FormControlLabel,
  Skeleton,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { client, type AccessSnapshot } from '../lib/api';
import { tokens } from '../theme';

/** Admin → Access: turn IP restriction on/off and manage the allowlist. */
export function AdminAccessPanel() {
  const [snap, setSnap] = useState<AccessSnapshot | null>(null);
  const [cidr, setCidr] = useState('');
  const [label, setLabel] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    client
      .adminAccess()
      .then(setSnap)
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load'));
  }, []);

  async function run(action: () => Promise<AccessSnapshot>, done?: string) {
    setError('');
    setInfo('');
    setBusy(true);
    try {
      setSnap(await action());
      if (done) setInfo(done);
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong');
      return false;
    } finally {
      setBusy(false);
    }
  }

  async function add(e: FormEvent) {
    e.preventDefault();
    const ok = await run(() => client.addAccessEntry(cidr, label), `Added ${cidr.trim()}.`);
    if (ok) {
      setCidr('');
      setLabel('');
    }
  }

  if (!snap) {
    return error ? <Alert severity="error">{error}</Alert> : <Skeleton variant="rounded" height={320} />;
  }

  const myIpListed = snap.allowed;

  return (
    <Stack spacing={3}>
      {error && <Alert severity="error">{error}</Alert>}
      {info && <Alert severity="success">{info}</Alert>}

      <Card>
        <CardContent sx={{ p: { xs: 2.5, md: 3.5 } }}>
          <Stack direction={{ xs: 'column', md: 'row' }} gap={3} justifyContent="space-between">
            <Stack spacing={1} maxWidth={640}>
              <Typography variant="h6">Restrict access by IP address</Typography>
              <Typography color="text.secondary">
                {snap.enforce
                  ? 'On — only the networks below can open the portal. Everyone else sees an Access denied screen, including the sign-in page.'
                  : 'Off — anyone can reach the sign-in page. Networks below can still browse applications and status without signing in.'}
              </Typography>
            </Stack>
            <FormControlLabel
              sx={{ flex: 'none', alignSelf: { md: 'center' }, mr: 0 }}
              control={
                <Switch
                  checked={snap.enforce}
                  disabled={busy}
                  onChange={(e) =>
                    run(
                      () => client.setAccessEnforce(e.target.checked),
                      e.target.checked ? 'Restriction is on.' : 'Restriction is off.',
                    )
                  }
                />
              }
              label={<Typography fontWeight={600}>{snap.enforce ? 'On' : 'Off'}</Typography>}
            />
          </Stack>

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            gap={2}
            alignItems={{ sm: 'center' }}
            justifyContent="space-between"
            sx={{ mt: 3, p: 2, borderRadius: 2, bgcolor: myIpListed ? tokens.successSoft : tokens.warnSoft }}
          >
            <Typography color={myIpListed ? '#054F31' : '#7A2E0E'}>
              Your current address is <strong>{snap.ip || 'unknown'}</strong> —{' '}
              {myIpListed ? 'allowed.' : 'not on the list.'}
            </Typography>
            {!myIpListed && snap.ip && (
              <Button
                variant="contained"
                size="small"
                disabled={busy}
                onClick={() => run(() => client.addAccessEntry(snap.ip, 'Added from admin'), `Added ${snap.ip}.`)}
              >
                Add my address
              </Button>
            )}
          </Stack>
        </CardContent>
      </Card>

      <Card>
        <CardContent sx={{ p: { xs: 2.5, md: 3.5 } }}>
          <Typography variant="h6" gutterBottom>
            Allowed networks
          </Typography>
          <Typography color="text.secondary" mb={2.5}>
            Single addresses like 203.0.113.10, or ranges like 203.0.113.0/24. IPv6 works too.
          </Typography>

          <Box sx={{ border: `1px solid ${tokens.divider}`, borderRadius: 2, overflow: 'hidden' }}>
            {snap.config.map((n) => (
              <Stack
                key={`cfg-${n}`}
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent="space-between"
                alignItems={{ sm: 'center' }}
                gap={1}
                sx={{ px: 2.5, py: 2, borderBottom: `1px solid ${tokens.divider}`, bgcolor: tokens.subtle }}
              >
                <Box>
                  <Typography component="code" sx={{ fontFamily: 'ui-monospace, Menlo, monospace', fontWeight: 600, color: tokens.ink }}>
                    {n}
                  </Typography>
                  <Typography fontSize={14} color="text.secondary">
                    Always allowed · set in wrangler.toml (ALLOWED_IPS)
                  </Typography>
                </Box>
                <Typography fontSize={13} color="text.secondary">
                  Locked
                </Typography>
              </Stack>
            ))}
            {snap.entries.map((e) => (
              <Stack
                key={e.id}
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent="space-between"
                alignItems={{ sm: 'center' }}
                gap={1}
                sx={{ px: 2.5, py: 2, borderBottom: `1px solid ${tokens.divider}` }}
              >
                <Box minWidth={0}>
                  <Typography component="code" sx={{ fontFamily: 'ui-monospace, Menlo, monospace', fontWeight: 600, color: tokens.ink }}>
                    {e.cidr}
                  </Typography>
                  <Typography fontSize={14} color="text.secondary">
                    {e.label || 'No label'} · added {new Date(e.created_at).toLocaleDateString()}
                    {e.created_by ? ` by ${e.created_by}` : ''}
                  </Typography>
                </Box>
                <Button
                  size="small"
                  color="error"
                  disabled={busy}
                  onClick={() => {
                    if (window.confirm(`Remove ${e.cidr} from the allowlist?`)) {
                      void run(() => client.deleteAccessEntry(e.id), `Removed ${e.cidr}.`);
                    }
                  }}
                  aria-label={`Remove ${e.cidr}`}
                >
                  Remove
                </Button>
              </Stack>
            ))}
            {snap.config.length === 0 && snap.entries.length === 0 && (
              <Typography sx={{ px: 2.5, py: 2 }} color="text.secondary">
                No networks yet.
              </Typography>
            )}
          </Box>

          <Stack component="form" onSubmit={add} direction={{ xs: 'column', sm: 'row' }} gap={1.5} mt={3} alignItems={{ sm: 'flex-start' }}>
            <TextField
              label="IP address or range"
              placeholder="203.0.113.0/24"
              value={cidr}
              onChange={(e) => setCidr(e.target.value)}
              required
              sx={{ flex: 1 }}
            />
            <TextField
              label="Label (optional)"
              placeholder="e.g. Penang plant"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              sx={{ flex: 1 }}
            />
            <Button type="submit" variant="contained" disabled={busy} sx={{ minHeight: 56 }}>
              Add
            </Button>
          </Stack>
        </CardContent>
      </Card>
    </Stack>
  );
}
